import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherTrash2, FeatherUpload } from "@subframe/core";
import { useRef, useState } from "react";

import type { DisplayCompliancePhoto } from "@/hooks/use-offline-compliance-completion";

interface Props {
  photos: DisplayCompliancePhoto[];
  onAddPhoto: (file: File) => Promise<void>;
  onDeletePhoto: (photoId: string) => Promise<void>;
  required: boolean;
}

/**
 * Universal evidence upload field. Required vs optional is communicated
 * structurally (visual weight) — when required, the dropzone is full-height
 * and labelled clearly; when optional, it stays tucked behind a single
 * "Add evidence" affordance until the user chooses to expand it.
 *
 * Accepts images and PDFs (the most common evidence types — photos of
 * inspections, scanned certificates).
 */
export function EvidenceUploadField({
  photos,
  onAddPhoto,
  onDeletePhoto,
  required,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState(required || photos.length > 0);
  const [busy, setBusy] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      for (const file of files) {
        await onAddPhoto(file);
      }
    } finally {
      setBusy(false);
      // Reset so the same file can be re-selected if the user removes and re-adds.
      e.target.value = "";
    }
  };

  // Optional + collapsed: render a low-weight inline trigger.
  if (!required && !expanded) {
    return (
      <div className="flex w-full flex-col items-start">
        <button
          type="button"
          className="text-body font-body text-primary-navy-800 underline"
          onClick={() => setExpanded(true)}
        >
          + Add photo evidence (optional)
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <div className="flex w-full items-center justify-between">
        <label className="text-body-bold font-body-bold text-neutral-900">
          Evidence
          {required && <span className="text-error-700 ml-1">*</span>}
        </label>
        {!required && (
          <button
            type="button"
            className="text-caption font-caption text-subtext-color underline"
            onClick={() => setExpanded(false)}
          >
            Hide
          </button>
        )}
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {required
          ? "Required — upload a photo or document to confirm this task."
          : "Optional — anything that would help an auditor."}
      </span>

      {/* Dropzone */}
      <div
        className={`flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed py-8 text-center ${
          required
            ? "border-primary-navy-800 bg-primary-navy-50"
            : "border-neutral-border bg-neutral-50"
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        <FeatherUpload className="h-6 w-6 text-subtext-color" />
        <span className="text-body font-body text-neutral-900">
          {busy ? "Uploading…" : "Tap to add photos or documents"}
        </span>
        <span className="text-caption font-caption text-subtext-color">
          Images or PDF
        </span>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Thumbnail grid */}
      {photos.length > 0 && (
        <div className="flex w-full flex-wrap gap-3 pt-2">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-md border border-neutral-border bg-neutral-100"
            >
              {photo.mimeType.startsWith("image/") && photo.blobUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={photo.blobUrl}
                  alt={photo.fileName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="px-2 text-center text-caption font-caption text-subtext-color">
                  {photo.fileName.length > 16
                    ? `${photo.fileName.slice(0, 14)}…`
                    : photo.fileName}
                </span>
              )}
              <IconButton
                className="absolute right-1 top-1 bg-white"
                size="small"
                icon={<FeatherTrash2 />}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDeletePhoto(photo.id);
                }}
                aria-label={`Remove ${photo.fileName}`}
              />
              {photo.syncState === "pending" && (
                <span className="absolute bottom-0 left-0 right-0 bg-amber-100 px-1 text-caption font-caption text-amber-900">
                  Syncing…
                </span>
              )}
              {photo.syncState === "error" && (
                <span className="absolute bottom-0 left-0 right-0 bg-error-100 px-1 text-caption font-caption text-error-700">
                  Failed
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
